import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';

const FavoritesContext = createContext(null);

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

export function FavoritesProvider({ children }) {
  const { user, getAuthHeader } = useAuth();
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchFavorites = useCallback(async () => {
    if (!user) {
      setFavorites([]);
      return;
    }
    setLoading(true);
    try {
      const headers = await getAuthHeader();
      const r = await fetch(`${API_URL}/favorites`, { headers });
      if (r.ok) {
        const data = await r.json();
        setFavorites(data);
      }
    } catch (e) {
      console.error('Fetch favorites error:', e);
    } finally {
      setLoading(false);
    }
  }, [user, getAuthHeader]);

  useEffect(() => {
    fetchFavorites();
  }, [fetchFavorites]);

  const addFavorite = useCallback(async (team) => {
    if (!user) return;
    setFavorites(prev => [
      { team_id: team.id, team_name: team.name, team_logo: team.logo, league_id: team.leagueId, league_name: team.leagueName, added_at: Date.now() },
      ...prev.filter(f => f.team_id !== team.id)
    ]);
    try {
      const headers = await getAuthHeader();
      await fetch(`${API_URL}/favorites`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify(team)
      });
    } catch (e) {
      console.error('Add favorite error:', e);
      fetchFavorites();
    }
  }, [user, getAuthHeader, fetchFavorites]);

  const removeFavorite = useCallback(async (teamId) => {
    if (!user) return;
    setFavorites(prev => prev.filter(f => f.team_id !== teamId));
    try {
      const headers = await getAuthHeader();
      await fetch(`${API_URL}/favorites/${teamId}`, {
        method: 'DELETE',
        headers
      });
    } catch (e) {
      console.error('Remove favorite error:', e);
      fetchFavorites();
    }
  }, [user, getAuthHeader, fetchFavorites]);

  const isFavorite = useCallback((teamId) => {
    return favorites.some(f => f.team_id === teamId);
  }, [favorites]);

  const toggleFavorite = useCallback((team) => {
    if (isFavorite(team.id)) {
      removeFavorite(team.id);
    } else {
      addFavorite(team);
    }
  }, [isFavorite, addFavorite, removeFavorite]);

  return (
    <FavoritesContext.Provider value={{
      favorites,
      loading,
      isFavorite,
      toggleFavorite,
      addFavorite,
      removeFavorite,
      refresh: fetchFavorites
    }}>
      {children}
    </FavoritesContext.Provider>
  );
}

export function useFavorites() {
  const ctx = useContext(FavoritesContext);
  if (!ctx) throw new Error('useFavorites must be used within FavoritesProvider');
  return ctx;
}
